import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getActiveDialSessions } from 'shared/api';
import { RejectError } from 'shared/api/axios';
import type { AppState } from 'shared/store/reducers';
import { DialSession, DialTask } from 'types/outbound';
import { LiveListenDelayOptions, LiveListenState } from 'types/livelisten';

export interface LiveListenDialSession extends DialSession {
  liveListenEnabled: boolean;
  ended: boolean;
}

interface LiveListenDialSessionStatePayload {
  id: number;
  liveListenEnabled: boolean;
}

interface LiveListenDialSessionIdsPayload {
  liveListenDialSessionId: number | null;
  salesfloorRoomId: string | null;
}

const LOCAL_STORAGE_AUTO_LISTEN_DELAY_KEY = 'autoListenDelay';
const storedAutoListenDelay = localStorage.getItem(LOCAL_STORAGE_AUTO_LISTEN_DELAY_KEY);

const initialState: {
  dialSessions: LiveListenDialSession[];
  autoListenState: LiveListenState;
  autoListenDelay: LiveListenDelayOptions;
  liveListenDialSessionId: number | null;
  salesfloorRoomId: string | null;
  joining: boolean;
} = {
  // TODO: consider making it a hash (not a big deal, there will only be a few of those)
  dialSessions: [],
  autoListenState: LiveListenState.OFF,
  autoListenDelay: storedAutoListenDelay
    ? parseInt(storedAutoListenDelay, 10)
    : LiveListenDelayOptions.DELAY_NONE,
  liveListenDialSessionId: null,
  salesfloorRoomId: null,
  joining: false,
};

export const fetchActiveDialSessions = createAsyncThunk<
  void,
  void,
  { state: AppState; rejectValue: RejectError }
>('livelisten/fetchActiveDialSessions', async (_, { dispatch }) => {
  const r = await getActiveDialSessions();

  dispatch(livelistenSlice.actions.setActiveDialSessions(r.data));
});

const livelistenSlice = createSlice({
  name: 'livelisten',
  initialState,
  reducers: {
    setActiveDialSessions(state, action: PayloadAction<DialSession[]>) {
      state.dialSessions = action.payload.map(
        (ds: DialSession) =>
          ({
            liveListenEnabled: true,
            ended: false,
            ...ds,
          } as LiveListenDialSession),
      );
    },
    setAutoListenState(state, action: PayloadAction<LiveListenState>) {
      state.autoListenState = action.payload;
    },
    setAutoListenDelay(state, action: PayloadAction<LiveListenDelayOptions>) {
      state.autoListenDelay = action.payload;
      localStorage.setItem(
        LOCAL_STORAGE_AUTO_LISTEN_DELAY_KEY,
        (action.payload as unknown) as string,
      );
    },
    setLiveListenDialSessionIds(
      state,
      action: PayloadAction<LiveListenDialSessionIdsPayload>,
    ) {
      state.liveListenDialSessionId = action.payload.liveListenDialSessionId;
      state.salesfloorRoomId = action.payload.salesfloorRoomId;
    },
    setJoining(state, action: PayloadAction<boolean>) {
      state.joining = action.payload;
    },
    toggleLiveListenDialSessionEnabled(
      state,
      action: PayloadAction<LiveListenDialSessionStatePayload>,
    ) {
      state.dialSessions = state.dialSessions.map(dialSession => {
        if (dialSession.id === action.payload.id) {
          return {
            ...dialSession,
            liveListenEnabled: action.payload.liveListenEnabled,
          };
        } else {
          return dialSession;
        }
      });
    },
    addDialSession(state, action: PayloadAction<LiveListenDialSession>) {
      state.dialSessions = [
        ...state.dialSessions,
        { ...action.payload, liveListenEnabled: true, ended: false },
      ];
    },
    removeDialSession(state, action: PayloadAction<LiveListenDialSession>) {
      state.dialSessions = state.dialSessions.map(dialSession =>
        dialSession.id === action.payload.id
          ? {
              ...dialSession,
              ended: true,
            }
          : dialSession,
      );
    },
    updateOrAddDialSessionTask(
      state,
      action: PayloadAction<{ dialSessionId: number; dialTask: DialTask }>,
    ) {
      state.dialSessions = state.dialSessions.map(dialSession =>
        dialSession.id === action.payload.dialSessionId
          ? {
              ...dialSession,
              tasksStarted:
                dialSession.tasksStarted &&
                dialSession.tasksStarted.find(
                  dialTask => dialTask.id === action.payload.dialTask.id,
                )
                  ? dialSession.tasksStarted?.map(dialTask =>
                      dialTask.id === action.payload.dialTask.id
                        ? action.payload.dialTask
                        : dialTask,
                    )
                  : (dialSession.tasksStarted = [
                      ...(dialSession.tasksStarted ? dialSession.tasksStarted : []),
                      action.payload.dialTask,
                    ]),
            }
          : dialSession,
      );
    },
  },
});

export const {
  addDialSession,
  removeDialSession,
  updateOrAddDialSessionTask,
  setAutoListenState,
  setAutoListenDelay,
  setJoining,
  setLiveListenDialSessionIds,
  toggleLiveListenDialSessionEnabled,
} = livelistenSlice.actions;

export default livelistenSlice.reducer;
